import { QueryClient } from "@tanstack/react-query";
import { del, get, set } from "idb-keyval";

// Cache expiry: 24 hours
const STALE_TIME = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// Garbage collection time: 7 days (how long to keep unused data in cache)
const GC_TIME = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

// Create the query client with default options
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: STALE_TIME,
      gcTime: GC_TIME,
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      refetchOnMount: true, // Refetch on mount if data is stale (older than staleTime)
      refetchOnReconnect: true, // Refetch when network reconnects
      retry: 2, // Retry failed requests twice
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
});

// Create IndexedDB persister for cache persistence across page refreshes
// Using IndexedDB instead of localStorage to handle large datasets (50MB+ capacity)
export const persister = {
  persistClient: async (client: any) => {
    try {
      const queries = Array.from(queryClient.getQueryCache().getAll()).map(
        (query) => ({
          queryKey: query.queryKey,
          queryHash: query.queryHash,
          state: {
            data: query.state.data,
            dataUpdatedAt: query.state.dataUpdatedAt,
            error: query.state.error,
            errorUpdatedAt: query.state.errorUpdatedAt,
            fetchStatus: query.state.fetchStatus,
            status: query.state.status,
          },
        })
      );

      const data = {
        version: CACHE_VERSION,
        timestamp: Date.now(),
        clientState: { queries },
      };

      console.log("💾 PERSISTING cache to IndexedDB:", {
        version: CACHE_VERSION,
        timestamp: new Date().toISOString(),
        queries: queries.length,
        dataSize: JSON.stringify(data).length,
      });

      await set("campaign_app_query_cache", data);
    } catch (error) {
      console.error("Failed to persist cache:", error);
    }
  },

  restoreClient: async () => {
    try {
      const cached = await get("campaign_app_query_cache");
      if (!cached) {
        console.log("🔄 No cache found in IndexedDB");
        return;
      }

      const { version, clientState, timestamp } = cached;

      // Check cache version - clear if outdated
      if (version !== CACHE_VERSION) {
        console.log(
          `⚠️ Cache version mismatch (${version} !== ${CACHE_VERSION}), clearing`
        );
        await del("campaign_app_query_cache");
        return;
      }

      const age = Date.now() - timestamp;
      const ageHours = Math.floor(age / (1000 * 60 * 60));

      console.log("🔄 RESTORING cache from IndexedDB:", {
        version,
        queries: clientState.queries?.length || 0,
        age: `${ageHours}h`,
      });

      if (age > 7 * 24 * 60 * 60 * 1000) {
        console.log("⚠️ Cache expired (> 7 days), clearing");
        await del("campaign_app_query_cache");
        return;
      }

      clientState.queries?.forEach((query: any) => {
        // Use setQueryData with updatedAt option to preserve the original timestamp
        // This is crucial - if we don't preserve the old timestamp, TanStack Query
        // will think the data is fresh and won't refetch when stale
        queryClient.setQueryData(query.queryKey, query.state.data, {
          updatedAt: query.state.dataUpdatedAt,
        });

        // Log each query's staleness
        const dataAge = Date.now() - query.state.dataUpdatedAt;
        const dataAgeHours = Math.floor(dataAge / (1000 * 60 * 60));
        const isStale = dataAge > STALE_TIME;
        console.log(
          `  📦 ${JSON.stringify(query.queryKey)}: ${dataAgeHours}h old ${isStale ? "(STALE - will refetch)" : "(fresh)"}`
        );
      });

      console.log("✅ RESTORED cache successfully");
    } catch (error) {
      console.error("Failed to restore cache:", error);
    }
  },

  removeClient: async () => {
    await del("campaign_app_query_cache");
  },
};

// Cache buster version - increment this to invalidate all cached data
export const CACHE_VERSION = "v6"; // Added hasHighLikelihoodResident field
